import React from "react";
import { connect, DispatchProp } from "react-redux";
import { Page } from "../../components/page/Page";
import { PageModel } from "../../models/PageModel";
import { QuestionModel } from "../../models/QuestionModel";
import { StoreState } from "../../models/states/StoreState";
import { Voxco } from "../../public";
import { ThemeModel } from "../../models/ThemeModel";
import { QuestionnaireModel } from "../../models/QuestionnaireModel";
import { translate } from "../../models/utilities/translationUtilities";
import { ValidationResultModel } from "../../models/ValidationResultModel";
import { getContainerDiv } from "../../utils";
import { addVoxcoAgentAuditLog } from "../../utils/voxcoAgentHelper";
import {
    selectNavigationDisabled,
    selectNavigationMessage,
} from "../../redux/selectors/interview/navigation";
import { getMaxLoopSize } from "../../models/utilities/blockUtilities";
import { PageTitleAnnouncer } from "../../components/shared/PageTitleAnnouncer";

interface OwnProps {
    isDesktopPreviewView?: boolean;
}

interface StateProps {
    isValid: boolean;
    message: string | Error;
    validation: ValidationResultModel;
    questionnaire: QuestionnaireModel;
    isNavigationDisabled: boolean;
    page?: PageModel;
    questions: QuestionModel[];
    language: string;
    title: string;
    theme: ThemeModel;
    useClientValidation: boolean;
    parent?: string;
}

type Props = OwnProps & StateProps;

class PageContainerImpl extends React.PureComponent<Props & DispatchProp> {
    componentDidMount() {
        const { page } = this.props;
        if (page !== undefined && page.isReady) {
            this.onPageReady(page);
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps: StateProps) {
        const { page } = this.props;
        if (page !== nextProps.page) {
            const surveyEngine = getContainerDiv();
            if (surveyEngine !== undefined) surveyEngine.scrollTop = 0;
        }
    }

    hasIncompleteAnswer = (): boolean => {
        const { validation } = this.props;
        if (!validation || !validation.errorsByVariableName) {
            return false;
        }
        return Object.values(validation.errorsByVariableName).some((errors) =>
            errors.some((error) => error.key === "IncompleteAnswer"),
        );
    };

    componentDidUpdate(prevProps: Props) {
        const { page: prevPage } = prevProps;
        const { page } = this.props;
        if (page !== prevPage && page !== undefined && page.isReady) {
            this.onPageReady(page);
            // Reset focus to #root on actual page navigation. Previously this lived
            // in Page.tsx onNavigation but ran on every Next click — including
            // validation failures that don't navigate — causing NVDA to cascade-read
            // the entire page after each alert.
            // Do not reset focus if there are incomplete answers (validation errors).
            if (!this.hasIncompleteAnswer()) {
                const rootEl = document.getElementById("root");
                if (rootEl) {
                    rootEl.setAttribute("tabindex", "-1");
                    rootEl.focus();
                }
            }
        }
    }

    logQuestionLoadedAudit = (page: PageModel) => {
        const questionNumber = page.questions?.length > 0 ? page.questions[0].index + 1 : undefined;
        const event =
            questionNumber !== undefined ? `QuestionLoaded: ${questionNumber}` : "QuestionLoaded";
        addVoxcoAgentAuditLog(event, this.props.parent);
    };

    onPageReady(page: PageModel) {
        Voxco.trigger("pageReady", page);
        this.logQuestionLoadedAudit(page);
    }

    render() {
        const { message, page, title } = this.props;
        return (
            <div className="pageContainer">
                <PageTitleAnnouncer title={title} isReady={Boolean(page?.isReady)} />
                {(message || page?.isReady) && (
                    <Page
                        {...this.props}
                        matrixIndex={page?.loopIndex ?? 1}
                        progress={page?.progress ?? 0}
                    />
                )}
            </div>
        );
    }
}

/**
 * Derives the per-page browser tab title (WCAG 2.4.2). Today every page shows the same
 * survey title; instead we apply the fallback chain (most specific first):
 *   single question's Page Title -> Block.PageTitle (active language) -> Survey Title.
 * For looped blocks an "Iteration N" suffix keeps repeated pages unique.
 */
const getPageTitle = (
    state: StoreState,
    currentPage: PageModel | undefined,
    language: string,
): string => {
    const { navigation, questionnaire, theme } = state.interview;
    const surveyTitle = translate(theme, "SurveyTitle", language) || state.interview.surveyTitle || "";

    if (currentPage == null) {
        return surveyTitle;
    }

    const block =
        navigation?.randomizedBlocks?.[currentPage.blockIndex] ??
        questionnaire?.blocks?.[currentPage.blockIndex];

    // Single-question (auto page break) pages: the question's Page Title takes precedence so each
    // page stays unique. Otherwise fall back to the block's Page Title, then the survey title.
    let title =
        currentPage.questions?.length === 1
            ? translate(currentPage.questions[0], "PageTitle", language) || ""
            : "";

    if (!title && block) {
        title = translate(block, "PageTitle", language) || "";
    }

    if (!title) {
        title = surveyTitle;
    }

    // Looped blocks repeat the same block; keep titles unique across iterations.
    if (block && getMaxLoopSize(block) > 1) {
        const iteration = `Iteration ${currentPage.loopIndex}`;
        title = title ? `${title} - ${iteration}` : iteration;
    }

    return title;
};

const mapStateToProps = (state: StoreState, ownProps: OwnProps): Props => {
    const { navigation, questionnaire, validation, language, theme } = state.interview;
    const { currentPage } = navigation ?? {};
    return {
        message: selectNavigationMessage(state),
        questionnaire,
        validation,
        isNavigationDisabled: selectNavigationDisabled(state),
        isValid: validation.isValid,
        page: currentPage,
        questions: currentPage?.questions ?? [],
        language,
        theme,
        title: getPageTitle(state, currentPage, language),
        useClientValidation: state.interview.useClientValidation,
        isDesktopPreviewView: ownProps.isDesktopPreviewView,
        parent: state.application.parent,
    };
};

export const PageContainer = connect(mapStateToProps)(PageContainerImpl);
